import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import { History, User, Calendar, TrendingUp, Clock, Filter, Home } from 'lucide-react';
import PageContainer from '../layout/PageContainer';

interface LoginRecord {
  id: number;
  staff_id: number;
  staff_name: string;
  username: string;
  login_type: string;
  login_time: string;
  ip_address: string;
  user_agent: string;
  success: boolean;
  icon: string;
  icon_color: string;
}

interface TodayStatus {
  id: number;
  name: string;
  username: string;
  icon: string;
  icon_color: string;
  last_login: string | null;
  login_count: number;
}

interface LoginStats {
  date: string;
  unique_users: number;
  total_logins: number;
  pin_logins: number;
  password_logins: number;
}

// 日本時間（JST）で今日の日付を取得
const getJSTDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const LoginHistory: React.FC = () => {
  const navigate = useNavigate();
  const [loginHistory, setLoginHistory] = useState<LoginRecord[]>([]);
  const [todayStatus, setTodayStatus] = useState<TodayStatus[]>([]);
  const [loginStats, setLoginStats] = useState<LoginStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'history' | 'today' | 'stats'>('history');
  const [selectedDate, setSelectedDate] = useState<string>(getJSTDate());

  useEffect(() => {
    fetchData();
  }, [activeTab, selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'history':
          await fetchLoginHistory();
          break;
        case 'today':
          await fetchTodayStatus();
          break;
        case 'stats':
          await fetchLoginStats();
          break;
      }
    } catch (error) {
      console.error('データ取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLoginHistory = async () => {
    try {
      console.log('📅 ログイン履歴取得:', { selectedDate, limit: 100 });
      const response = await apiClient.get('/login-history', {
        params: { limit: 100, date: selectedDate }
      });
      console.log('📊 API Response:', response.data);
      if (response.data.success) {
        console.log('✅ 取得件数:', response.data.data.length);
        setLoginHistory(response.data.data);
      }
    } catch (error) {
      console.error('ログイン履歴取得エラー:', error);
    }
  };

  const fetchTodayStatus = async () => {
    try {
      const response = await apiClient.get('/login-history/today');
      if (response.data.success) {
        setTodayStatus(response.data.data);
      }
    } catch (error) {
      console.error('本日のログイン状況取得エラー:', error);
    }
  };

  const fetchLoginStats = async () => {
    try {
      const response = await apiClient.get('/login-history/stats');
      if (response.data.success) {
        setLoginStats(response.data.data);
      }
    } catch (error) {
      console.error('ログイン統計取得エラー:', error);
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    // データベースの時刻は既に日本時間（JST）なので、そのまま表示
    return dateString.replace('T', ' ').substring(0, 19);
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return '-';
    // データベースの時刻は既に日本時間（JST）なので、時刻部分のみ抽出
    const timePart = dateString.includes('T')
      ? dateString.split('T')[1].substring(0, 8)
      : dateString.split(' ')[1]?.substring(0, 8) || dateString;
    return timePart;
  };

  const getUserAgent = (userAgent: string) => {
    if (!userAgent) return '不明';

    // ブラウザ判定
    let browser = '';
    let browserIcon = '';
    if (userAgent.includes('Edg/')) {
      browser = 'Edge';
      browserIcon = '🔷';
      const version = userAgent.match(/Edg\/(\d+)/);
      if (version) browser += ` ${version[1]}`;
    } else if (userAgent.includes('Chrome/')) {
      browser = 'Chrome';
      browserIcon = '🔵';
      const version = userAgent.match(/Chrome\/(\d+)/);
      if (version) browser += ` ${version[1]}`;
    } else if (userAgent.includes('Firefox/')) {
      browser = 'Firefox';
      browserIcon = '🦊';
      const version = userAgent.match(/Firefox\/(\d+)/);
      if (version) browser += ` ${version[1]}`;
    } else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome')) {
      browser = 'Safari';
      browserIcon = '🧭';
    }

    // OS判定
    let os = '';
    if (userAgent.includes('Windows NT 10')) {
      os = 'Win10';
    } else if (userAgent.includes('Windows NT 11')) {
      os = 'Win11';
    } else if (userAgent.includes('Mac OS X')) {
      os = 'macOS';
    } else if (userAgent.includes('Linux')) {
      os = 'Linux';
    } else if (userAgent.includes('Android')) {
      os = 'Android';
    } else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
      os = 'iOS';
    }

    // デバイス種別
    let device = '';
    if (userAgent.includes('Mobile')) {
      device = '📱';
    } else if (userAgent.includes('Tablet')) {
      device = '📱';
    } else {
      device = '💻';
    }

    return `${device} ${browserIcon} ${browser}${os ? ` (${os})` : ''}`;
  };

  return (
    <PageContainer>
      <div>
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <History className="h-8 w-8 text-blue-600" />
            ログイン履歴
          </h1>
          <div className="flex items-center gap-4">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 border rounded-md"
            />
            <button
              onClick={() => navigate('/home')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Home className="w-5 h-5" />
              ホーム
            </button>
          </div>
        </div>

        {/* タブナビゲーション */}
        <div className="mb-6 border-b">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('history')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <History className="inline h-4 w-4 mr-1" />
              ログイン履歴
            </button>
            <button
              onClick={() => setActiveTab('today')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'today'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <User className="inline h-4 w-4 mr-1" />
              本日の状況
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stats'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <TrendingUp className="inline h-4 w-4 mr-1" />
              統計
            </button>
          </nav>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* ログイン履歴タブ */}
            {activeTab === 'history' && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        時刻
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        スタッフ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ログイン方法
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        デバイス
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        IPアドレス
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loginHistory && loginHistory.map((record) => (
                      <tr key={record.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <Clock className="inline h-4 w-4 text-gray-400 mr-1" />
                          {formatTime(record.login_time)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center text-lg mr-2"
                              style={{ backgroundColor: record.icon_color + '20' }}
                            >
                              {record.icon || '👤'}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {record.staff_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                @{record.username}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            record.login_type === 'PIN'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {record.login_type === 'PIN' ? '🔢 PIN' : '🔑 パスワード'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getUserAgent(record.user_agent || '')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.ip_address || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {loginHistory.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    選択された日付のログイン履歴はありません
                  </div>
                )}
              </div>
            )}

            {/* 本日の状況タブ */}
            {activeTab === 'today' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {todayStatus && todayStatus.map((staff) => (
                  <div key={staff.id} className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center text-2xl mr-3"
                          style={{ backgroundColor: staff.icon_color + '20' }}
                        >
                          {staff.icon || '👤'}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">
                            {staff.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            @{staff.username}
                          </div>
                        </div>
                      </div>
                      {staff.login_count > 0 && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                          ログイン済
                        </span>
                      )}
                    </div>
                    <div className="border-t pt-2">
                      <div className="text-sm text-gray-600">
                        <div className="flex justify-between">
                          <span>本日のログイン回数:</span>
                          <span className="font-semibold">{staff.login_count}回</span>
                        </div>
                        {staff.last_login && (
                          <div className="flex justify-between mt-1">
                            <span>最終ログイン:</span>
                            <span className="font-semibold">{formatTime(staff.last_login)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 統計タブ */}
            {activeTab === 'stats' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {loginStats && loginStats.length > 0 && loginStats.slice(0, 1).map((stat) => (
                    <React.Fragment key={stat.date}>
                      <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-gray-500">本日のログイン数</div>
                        <div className="text-2xl font-bold text-gray-900 mt-1">
                          {stat.total_logins}
                        </div>
                      </div>
                      <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-gray-500">ユニークユーザー</div>
                        <div className="text-2xl font-bold text-gray-900 mt-1">
                          {stat.unique_users}
                        </div>
                      </div>
                      <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-gray-500">PINログイン</div>
                        <div className="text-2xl font-bold text-purple-600 mt-1">
                          {stat.pin_logins}
                        </div>
                      </div>
                      <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-gray-500">パスワードログイン</div>
                        <div className="text-2xl font-bold text-blue-600 mt-1">
                          {stat.password_logins}
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-4 border-b">
                    <h3 className="text-lg font-semibold">過去30日間のログイン統計</h3>
                  </div>
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          日付
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ユニークユーザー
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          合計ログイン
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          PINログイン
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          パスワード
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {loginStats && loginStats.map((stat) => (
                        <tr key={stat.date}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(stat.date).toLocaleDateString('ja-JP')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {stat.unique_users}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {stat.total_logins}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600">
                            {stat.pin_logins}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                            {stat.password_logins}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default LoginHistory;